/**
 * useKbDecision — the one implementation of "which documents get synthesised".
 *
 * Extracted from `KbApprovalPanel` when the same decision gained a SECOND
 * surface (the intake review on `UploadView`). The parts worth centralising are
 * the ones that were each written to fix a real defect:
 *
 * - the **inverted `unchecked` set**, which is what makes "the app proposes, the
 *   Admin vetoes" the default rather than something a future edit must remember;
 * - the **synchronous `inFlight` ref**, not `mutation.isPending` — React state is
 *   async, so two clicks in one tick both read `false`, and react-query's `scope`
 *   QUEUES the second call rather than dropping it, which here means a second
 *   approval;
 * - the **`totalPages === 0` branch**, because a sum over documents that were
 *   never measured is 0, and "0 pagine da sintetizzare" reads as "these are
 *   empty" rather than "we never counted";
 * - **`report.ignored` is always surfaced**, never swallowed;
 * - the **closed window while a decision is in flight** — `toggle`/`setAll`
 *   refuse, and the success path does not reset the set. See `toggle`.
 *
 * Two callers with two layouts, one decision. A second copy of the above is how
 * one of them silently loses a fix the other keeps.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { apiErrorMessage } from "./api";
import {
  REQUIRED_ANALYZE_ROLES,
  useDecideKbForward,
  type KbForwardRefusal,
  type TenderDocument,
} from "./tenderApi";

/** States a document can be in once the gate has had its say. */
export const DECIDED_KB_STATES = new Set([
  "queued",
  "forwarding",
  "forwarded",
  "failed",
  "skipped",
]);

export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** `null` means never measured — render the absence, never a zero. */
export function pagesLabel(pageCount: number | null): string | null {
  if (pageCount === null) return null;
  return `${pageCount} ${plural(pageCount, "pagina", "pagine")}`;
}

/**
 * The opening lines of a document, trimmed for a list row.
 *
 * The stored block is up to 1200 characters and often carries the whole cover
 * sheet. Four lines is enough to tell a Capitolato from a Piano di manutenzione,
 * which is the only judgement being asked for here.
 */
export function recapLines(titleBlock: string | null, max = 4): string[] {
  if (!titleBlock) return [];
  return titleBlock
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, max);
}

export interface KbDecision {
  /** Proposed and still undecided, EXCLUDING the locked-in ones below. */
  optional: TenderDocument[];
  /**
   * Proposed, undecided, and not the user's to veto: a document that is also a
   * required analysis input. Omar, 2026-08-14 — these carry no checkbox and are
   * always included when the batch is confirmed.
   */
  lockedIn: TenderDocument[];
  /** Already decided, in either direction. */
  decided: TenderDocument[];
  /** Anything at all to show. */
  hasAnything: boolean;
  isChecked: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Scope with `ids` to the rows currently on screen — see the implementation. */
  setAll: (checked: boolean, ids?: string[]) => void;
  /** Ids that will be approved on submit — locked-in included. */
  approve: string[];
  /**
   * `KB-23` — the locked-in documents inside `approve`, as documents rather than
   * ids, so a surface can NAME them before the money is spent.
   *
   * Built from the same `vetoed` predicate `approve` is built from, three lines
   * apart, rather than re-derived by a caller: two matchers that must agree are
   * two matchers, and this project has already paid for that once
   * (`feedback_one_predicate_not_two_callers`). These are the documents that
   * make «Avvia solo la Knowledge Base (2)» read 2 when the user ticked 1.
   */
  lockedInApproved: TenderDocument[];
  skip: string[];
  /** Vetoed documents that have since become required by the analysis. */
  vetoedNowRequired: TenderDocument[];
  /** "12 documenti (di cui 2 senza conteggio pagine) · 34 pagine da sintetizzare" */
  summary: string;
  submit: () => Promise<void>;
  isPending: boolean;
  done: string | null;
  /**
   * `KB-23` — the filenames of the LOCKED-IN documents the server confirmed it
   * queued, captured at submit time.
   *
   * It exists because the sentence that explained them is derived from
   * `lockedIn`, which is derived from `pending` — and a successful submit is
   * precisely the event that empties `pending`. So the explanation unmounted in
   * the same render as the receipt appeared, and the reader was left with a
   * number and no reason for it. Omar hit this on his first run: he ticked one
   * document and read «2 documenti inviati».
   *
   * Read from `report.queued` and NOT from the ids we posted: the server is the
   * authority on what it actually moved, and an id it declined comes back in
   * `ignored`. Predicting here would let the receipt name a document that never
   * went.
   *
   * Empty on failure and empty before any submit, so "we sent something extra"
   * is never claimed on a run that sent nothing.
   */
  sentLockedIn: string[];
  /** Gate B — one line per document that did NOT go, with the server's reason.
   *  Detail under `done`, which carries the count. See `forwardReceipt`. */
  notSent: string[];
  /** The receipt's colour is a claim too: `ok` only when everything sent has
   *  arrived, `info` while something is still on its way, `err` if anything did
   *  not go. */
  receiptTone: ReceiptTone;
  error: string | null;
}

/**
 * The receipt's second line: which documents the decision INCLUDED that the user
 * never ticked.
 *
 * `null` when there are none — the caller renders nothing rather than an empty
 * reassurance.
 *
 * Gate B, 2026-09-28 — it said «è stato inviato anche», past tense, from the
 * reply's `queued`: the same promise-read-as-a-result the count line above it
 * made. The independent review measured the two side by side on one receipt:
 * «1 documento non inviato · rel.pdf — Invio non riuscito…» above «Oltre alla tua
 * selezione è stato inviato anche «rel.pdf»». It now says what the decision
 * CONTAINS, which is true whatever happens next; whether the document ARRIVED
 * is the count line's job, read off the row (`forwardReceipt`).
 */
export function sentLockedInNote(names: string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) {
    return `Oltre alla tua selezione è compreso anche «${names[0]}»: è richiesto dall'analisi e viene sempre incluso.`;
  }
  return `Oltre alla tua selezione sono compresi anche ${names.length} documenti richiesti dall'analisi, che vengono sempre inclusi (${names.join(", ")}).`;
}

export type ReceiptTone = "ok" | "info" | "err";

export interface ForwardReceipt {
  /** Count sentences. «inviato» appears ONLY for rows that say `forwarded`. */
  parts: string[];
  /** «file.pdf — reason», one per document that did not go. */
  notSent: string[];
  tone: ReceiptTone;
}

/** What a decision's reply said, kept so the receipt can be re-read against the rows. */
export interface ForwardOutcome {
  sent: string[];
  refused: KbForwardRefusal[];
  skipped: number;
  ignored: number;
}

/**
 * Gate B, 2026-09-28 — what happened to the documents a decision SENT, read off
 * their rows rather than off the reply.
 *
 * The reply's `queued` is a promise (`useDecideKbForward` says so in as many
 * words), and the receipt printed it in the past tense: «1 documento inviato
 * alla Knowledge Base». On QA a customer's 266-page Capitolato was reported sent
 * six times while a background guard skipped it seconds after each reply; the
 * sentence stayed on screen above the row it described, back in «Esclusi».
 *
 * So the past tense is earned per document, from the list this screen already
 * polls while anything is in flight (`P-78`):
 *
 * - `forwarded` → «inviato»;
 * - `failed`, or any row carrying the server's reason (`kbForwardNote`) → «non
 *   inviato», with that reason;
 * - anything else — `queued`, `forwarding`, or a row the list has not refreshed
 *   yet — → «in invio».
 *
 * The last line is why a stale row cannot read as a refusal: a document still
 * showing its pre-decision state (`pending`/`skipped`) with NO reason is the list
 * not having caught up, because every refusal the server makes carries its
 * sentence (`kb_forward.forward_note`). Refused at the decision itself → «non
 * inviato» with the reply's reason, and never counted as sent.
 */
export function forwardReceipt(
  sentIds: string[],
  refused: KbForwardRefusal[],
  documents: TenderDocument[],
): ForwardReceipt {
  const byId = new Map(documents.map((d) => [d.id, d]));
  let arrived = 0;
  let moving = 0;
  const notSent = refused.map((r) => `${r.filename} — ${r.reason}`);
  for (const id of sentIds) {
    const d = byId.get(id);
    if (d?.kbForwardState === "forwarded") arrived += 1;
    else if (d && (d.kbForwardState === "failed" || d.kbForwardNote)) {
      notSent.push(`${d.filename} — ${d.kbForwardNote ?? "Invio non riuscito."}`);
    } else moving += 1;
  }
  const parts: string[] = [];
  if (arrived > 0) {
    parts.push(
      `${arrived} ${plural(arrived, "documento inviato", "documenti inviati")} alla Knowledge Base`,
    );
  }
  if (moving > 0) {
    parts.push(
      `${moving} ${plural(moving, "documento in invio", "documenti in invio")} alla Knowledge Base`,
    );
  }
  if (notSent.length > 0) {
    parts.push(
      `${notSent.length} ${plural(notSent.length, "documento non inviato", "documenti non inviati")}`,
    );
  }
  return {
    parts,
    notSent,
    tone: notSent.length > 0 ? "err" : moving > 0 ? "info" : "ok",
  };
}

export function useKbDecision(documents: TenderDocument[], projectId: string | null): KbDecision {
  const decide = useDecideKbForward();
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // Gate B — the REPLY is state; the receipt is derived from it and the rows.
  const [outcome, setOutcome] = useState<ForwardOutcome | null>(null);
  // `KB-23`. Kept beside `done` and cleared on the same events, so the receipt
  // and its explanation can never be shown apart. See `sentLockedIn` on the
  // interface for why this is state rather than derived.
  const [sentLockedIn, setSentLockedIn] = useState<string[]>([]);
  const inFlight = useRef(false);

  const { optional, lockedIn, decided } = useMemo(() => {
    const pending = documents.filter((d) => d.kbForwardState === "pending");
    return {
      optional: pending.filter((d) => !REQUIRED_ANALYZE_ROLES.includes(d.role)),
      lockedIn: pending.filter((d) => REQUIRED_ANALYZE_ROLES.includes(d.role)),
      decided: documents.filter((d) => DECIDED_KB_STATES.has(d.kbForwardState)),
    };
  }, [documents]);

  const isChecked = (id: string): boolean => !unchecked.has(id);

  /**
   * The decision is CLOSED for the duration of the call that commits it.
   *
   * `submit()` reads the payload once, before its `await`, and the controls used
   * to stay live for the whole round trip: a veto placed inside that window was
   * on a document already snapshotted as approved, so the request carried it as
   * approved, and `setUnchecked(new Set())` on success then erased the tick the
   * user had just removed. They watched a checkbox come back and had no way to
   * know the document had gone to a tenant-wide store against their decision.
   *
   * The obvious repair — "clear only the ids we sent" — is IDENTICAL to the old
   * behaviour for exactly this scenario, because a mid-flight veto is on a
   * document that IS in the batch. So the input is removed rather than
   * reconciled, and the reset is gone (see `submit`).
   *
   * `inFlight.current` and not `decide.isPending`: the ref flips in the same
   * tick as the click, React state does not, and this file already carries that
   * lesson one guard up. The `disabled` attributes on the two surfaces are the
   * VISIBLE half of this rule — deliberately not the only half, because a rule
   * that lives in markup leaves no branch a test can reach.
   */
  function toggle(id: string): void {
    if (inFlight.current) return;
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Bulk select/deselect.
   *
   * `ids` scopes it to what the user can actually SEE. Without that, a bulk
   * control rendered beside a filter operated on the whole optional set: with a
   * filter narrowing the list to one row, "Deseleziona tutti" took the confirm
   * count from 3 to 0 — two documents vetoed off-screen by a click on a screen
   * showing one. A bulk action must never reach past the rows it sits above.
   */
  function setAll(checked: boolean, ids?: string[]): void {
    if (inFlight.current) return;
    const scope = ids ?? optional.map((d) => d.id);
    setUnchecked((prev) => {
      const next = new Set(prev);
      for (const id of scope) {
        if (checked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  /**
   * A veto follows the DOCUMENT, not the bucket it happens to be in.
   *
   * `lockedIn` membership is derived live from the document's role, and the role
   * is editable from the very row that carries the checkbox ("Correggi → Ruolo
   * nell'analisi"). So: untick a proposed document, then correct its type to one
   * of the four required roles, and it left `optional` for `lockedIn` — where
   * `approve` took every id unconditionally. The explicit veto became a Knowledge
   * Base approval, the checkbox was replaced by a green "sempre incluso" tick, and
   * `groupDocuments` moved the row into a group Passo 2 renders COLLAPSED, so it
   * left the screen entirely. Pressing Avvia then synthesised it into a
   * tenant-wide store against the user's stated decision.
   *
   * Honouring the veto here cannot drop the document from the ANALYSIS: the
   * endpoint this feeds writes `kb_forward_state` and `kb_forward_error` and
   * nothing else (`documents.py` `decide_kb_forward`), so `role` — the thing that
   * makes a document required — is untouched. Verified in the SQL, not assumed:
   * that is the whole reason this is safe to do in one line.
   */
  const vetoed = (d: TenderDocument): boolean => unchecked.has(d.id);
  // `KB-23`. Named here, beside `approve`, from the SAME predicate — see the
  // interface for why this is not left to the caller to re-derive.
  const lockedInApproved = lockedIn.filter((d) => !vetoed(d));
  const approve = [
    ...lockedIn.filter((d) => !vetoed(d)).map((d) => d.id),
    ...optional.filter((d) => !vetoed(d)).map((d) => d.id),
  ];
  const skip = [...lockedIn.filter(vetoed), ...optional.filter(vetoed)].map((d) => d.id);
  /** Documents the user vetoed that have since BECOME required. Non-empty means a
   *  decision is being honoured on a row that no longer shows a checkbox, which
   *  has to be said out loud on the step that commits it. */
  const vetoedNowRequired = lockedIn.filter(vetoed);

  /**
   * The SAME payload, read at call time rather than at render time.
   *
   * `submit()` is called from two places and they are not equivalent. The
   * panel's own "Conferma la selezione" invokes it synchronously from a click,
   * where the closure is current. `startRun` invokes it AFTER `await
   * beginAnalysis(...)` — a network round trip — and the checkboxes stay live
   * for the whole of it (`canDecide` is `isKbAdmin`, a constant). So a veto
   * placed while the analysis was starting was computed into a render the
   * suspended closure never saw, and the batch approved the document anyway:
   * the user watched a tick disappear, the request carried it as approved, and
   * `setUnchecked(new Set())` on success removed the evidence.
   *
   * A ref updated in an effect is what makes "what the user last saw" and "what
   * we sent" the same fact. Same medicine as `jobIdRef` on the screen above,
   * for the same reason.
   */
  const latest = useRef({ unchecked, optional, lockedIn });
  useEffect(() => {
    latest.current = { unchecked, optional, lockedIn };
  }, [unchecked, optional, lockedIn]);

  const approved = [...lockedIn.filter((d) => !vetoed(d)), ...optional.filter((d) => !vetoed(d))];
  const totalPages = approved.reduce((sum, d) => sum + (d.pageCount ?? 0), 0);
  const unmeasured = approved.filter((d) => d.pageCount === null).length;
  // NOTHING was measured — a different fact from "everything measured, and the
  // total is zero". Branching on `totalPages === 0` conflated the two, so a
  // genuinely 0-page PDF reported "conteggio pagine non disponibile" while its
  // own row printed "0 pagine". Counting the MEASURED documents separates them.
  const measured = approved.length - unmeasured;

  const summary =
    approve.length === 0
      ? "Nessun documento selezionato — confermando, nessuno verrà sintetizzato."
      : measured === 0
        ? `${approve.length} ${plural(approve.length, "documento", "documenti")} — conteggio pagine non disponibile`
        : /* `P-37`/B5 — the DOCUMENT count leads, then the pages. "1121 pagine
             da sintetizzare" is a number nobody can sanity-check; "18 documenti
             · 1121 pagine" can be compared against the list directly above it,
             which is the only way a person catches the batch being wrong before
             confirming it. The two neighbouring branches already led with the
             document count, so this one was also the odd one out. */
          /* The parenthetical sits on the DOCUMENT count, not trailing the page
             count. "18 documenti · 1121 pagine da sintetizzare (di cui 2 senza
             conteggio pagine)" reads "di cui" against *pagine*, which is the
             nearest noun and the wrong one — the 2 are documents. Beside the
             number they qualify, there is nothing to misread. */
          `${approve.length} ${plural(approve.length, "documento", "documenti")}` +
          (unmeasured > 0 ? ` (di cui ${unmeasured} senza conteggio pagine)` : "") +
          ` · ${totalPages} ${plural(totalPages, "pagina", "pagine")} da sintetizzare` +
          /* "di cui", not "+". The leading number is now DOCUMENTS (`P-37`/B5),
             and `unmeasured` is a subset of it — so `18 documenti … (+2
             documenti …)` read as twenty when the answer was eighteen. It was
             unambiguous while the leading number was pages, because pages and
             documents cannot be added. Three surfaces print this string,
             including `KbApprovalPanel`, which this change never opened. */
          "";

  async function submit(): Promise<void> {
    if (inFlight.current) return;
    const target = projectId ?? optional[0]?.projectId ?? lockedIn[0]?.projectId;
    if (!target) return;
    inFlight.current = true;
    setError(null);
    setOutcome(null);
    setSentLockedIn([]);
    // Read NOW, not from the render that produced this closure.
    const now = latest.current;
    const vetoedNow = (d: TenderDocument): boolean => now.unchecked.has(d.id);
    const approveNow = [
      ...now.lockedIn.filter((d) => !vetoedNow(d)).map((d) => d.id),
      ...now.optional.filter((d) => !vetoedNow(d)).map((d) => d.id),
    ];
    const skipNow = [...now.lockedIn.filter(vetoedNow), ...now.optional.filter(vetoedNow)].map(
      (d) => d.id,
    );
    try {
      const report = await decide.mutateAsync({
        projectId: target,
        approve: approveNow,
        skip: skipNow,
      });
      // The receipt itself is derived below, from this AND the rows — see
      // `forwardReceipt` for why the reply alone may not say «inviato».
      setOutcome({
        sent: report.queued,
        refused: report.refused,
        skipped: report.skipped.length,
        ignored: report.ignored.length,
      });
      // `KB-23`. The locked-in documents the SERVER says it queued — not the
      // ones we asked it to. An id it declined is in `report.ignored`, and a
      // receipt naming a document that never went is the same class of lie the
      // vanishing sentence was.
      const queuedIds = new Set(report.queued);
      setSentLockedIn(now.lockedIn.filter((d) => queuedIds.has(d.id)).map((d) => d.filename));
      // NO `setUnchecked(new Set())` here.
      //
      // Its only observable effect was destructive. On the happy path it is a
      // no-op: every document in the batch leaves `pending`, so it leaves
      // `optional`/`lockedIn` and its checkbox stops being rendered at all —
      // measured, not assumed. What it DID do was erase a veto the user placed
      // while the call was open, and un-veto a document the server reported as
      // `ignored` (decided elsewhere, still pending here).
      //
      // A veto follows the DOCUMENT, exactly as the comment above `vetoed` says.
      // Nothing about a request completing makes an earlier decision untrue.
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      inFlight.current = false;
    }
  }

  const receipt = forwardReceipt(outcome?.sent ?? [], outcome?.refused ?? [], documents);
  const decisionParts = [...receipt.parts];
  if (outcome && outcome.skipped > 0) {
    decisionParts.push(`${outcome.skipped} ${plural(outcome.skipped, "escluso", "esclusi")}`);
  }
  // Never silent. An ignored id means the document was decided elsewhere or
  // is no longer eligible — swallowing that is how someone concludes the
  // button does nothing.
  if (outcome && outcome.ignored > 0) {
    decisionParts.push(
      `${outcome.ignored} ${plural(outcome.ignored, "già deciso in precedenza", "già decisi in precedenza")}`,
    );
  }
  const done = outcome === null ? null : decisionParts.join(" · ") || "Nessuna modifica.";

  return {
    optional,
    lockedIn,
    decided,
    hasAnything: optional.length + lockedIn.length + decided.length > 0,
    isChecked,
    toggle,
    setAll,
    approve,
    skip,
    vetoedNowRequired,
    summary,
    submit,
    isPending: decide.isPending,
    done,
    sentLockedIn,
    lockedInApproved,
    notSent: receipt.notSent,
    receiptTone: receipt.tone,
    error,
  };
}

/**
 * `KB-19` — re-add documents that were left OUT of the Knowledge Base.
 *
 * Omar, 2026-09-02: *"excluding a document during the initial preparation
 * should not be a permanent decision. The user must be able to add it to the
 * Knowledge Base later from «Documenti del progetto»."*
 *
 * **The server has always allowed this and said so in writing.**
 * `documents.py` `decide_kb_forward` moves `approve` ids with
 * `allowed=(pending, skipped)` — verified in the SQL, not inferred from the
 * docstring beside it — and that docstring names the consumer:
 *
 * > *"the product requirement that this screen stays reachable from
 * > `DocumentsPage` so 'add this one too' always works — a decision not to
 * > synthesise something is a decision to hold off, not a door that locks."*
 *
 * Nothing in the browser ever read that guarantee. `useKbDecision` files a
 * `skipped` document under `decided`, only `optional` rows get a checkbox, and
 * `approve` is built from `lockedIn + optional` — so a skipped id could not
 * enter the payload even if something offered it. Textbook
 * `feedback_a_guarantee_needs_a_consumer`: the guarantee shipped, the consumer
 * was named, and no one wrote it.
 *
 * **A separate hook, and the reason is the DEFAULT, not the layout.** Its
 * sibling starts every row CHECKED, because there the app proposes and the
 * admin vetoes. Here the user has already said no to these documents once.
 * Starting them checked would mean one click re-approving a whole set of
 * earlier refusals, and `forwarded` is terminal — there is no retract. So this
 * one starts with NOTHING selected and is opt-in per document. Two opposite
 * defaults is exactly the kind of difference that must not hide inside one
 * shared function with a flag.
 *
 * **`failed` is deliberately NOT offered.** The server's allow-list is
 * `(pending, skipped)`, so a failed id would come back in `ignored` and the
 * click would do nothing while looking like it worked. Retrying a failed
 * synthesis is a real gap and is recorded as its own item rather than faked
 * here.
 */
export interface KbReAdd {
  /** Previously excluded and re-addable. `skipped` only — see above. */
  excluded: TenderDocument[];
  /** Decided and NOT re-addable: in the KB, on their way, or failed. */
  locked: TenderDocument[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  selected: string[];
  /** "3 documenti · 42 pagine da sintetizzare", or null when nothing is picked. */
  summary: string | null;
  submit: () => Promise<void>;
  isPending: boolean;
  done: string | null;
  /** Gate B — see `KbDecision.notSent`. */
  notSent: string[];
  receiptTone: ReceiptTone;
  error: string | null;
}

/** States that mean "already decided and not re-addable from here". */
const LOCKED_KB_STATES = new Set(["queued", "forwarding", "forwarded", "failed"]);

export function useKbReAdd(documents: TenderDocument[], projectId: string | null): KbReAdd {
  const decide = useDecideKbForward();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ForwardOutcome | null>(null);
  /* Synchronous, not `decide.isPending`. React state is async, so two clicks in
     one tick both read `false` and both call through — and react-query's scope
     QUEUES the second rather than dropping it, which here means paying for a
     second synthesis. Its sibling carries this ref for exactly this reason. */
  const inFlight = useRef(false);

  const { excluded, locked } = useMemo(
    () => ({
      excluded: documents.filter((d) => d.kbForwardState === "skipped"),
      locked: documents.filter((d) => LOCKED_KB_STATES.has(d.kbForwardState)),
    }),
    [documents],
  );

  /* The selection is scoped to what is still re-addable. A document approved in
     another tab leaves `excluded` on the next refetch, and without this its id
     would stay in the set and be re-sent — landing in `ignored`, which is safe,
     but the count beside the button would have been counting it all along. */
  const selectedIds = excluded.filter((d) => selected.has(d.id)).map((d) => d.id);

  function toggle(id: string): void {
    if (inFlight.current) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const chosen = excluded.filter((d) => selected.has(d.id));
  const pages = chosen.reduce((sum, d) => sum + (d.pageCount ?? 0), 0);
  const unmeasured = chosen.filter((d) => d.pageCount === null).length;
  const summary =
    chosen.length === 0
      ? null
      : chosen.length - unmeasured === 0
        ? `${chosen.length} ${plural(chosen.length, "documento", "documenti")} — conteggio pagine non disponibile`
        : `${chosen.length} ${plural(chosen.length, "documento", "documenti")}` +
          (unmeasured > 0 ? ` (di cui ${unmeasured} senza conteggio pagine)` : "") +
          ` · ${pages} ${plural(pages, "pagina", "pagine")} da sintetizzare`;

  async function submit(): Promise<void> {
    if (inFlight.current) return;
    const target = projectId ?? excluded[0]?.projectId;
    const ids = selectedIds;
    if (!target || ids.length === 0) return;
    inFlight.current = true;
    setError(null);
    setOutcome(null);
    try {
      const report = await decide.mutateAsync({ projectId: target, approve: ids, skip: [] });
      /* `ignored` is surfaced, never swallowed — same rule as its sibling. An
         id lands there when the server refused it (no longer `kb_ingest`, or
         already moved), and a silent partial success is how someone believes a
         document is on its way when it is not. */
      setOutcome({
        sent: report.queued,
        refused: report.refused,
        skipped: 0,
        ignored: report.ignored.length,
      });
      /* Only the ids we actually sent are cleared. A document ticked while the
         request was in flight is not in `ids`, and clearing the whole set would
         silently drop that tick — the mirror of the defect its sibling records
         under `toggle`. */
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      inFlight.current = false;
    }
  }

  const receipt = forwardReceipt(outcome?.sent ?? [], outcome?.refused ?? [], documents);
  const ignored = outcome?.ignored ?? 0;
  const done =
    outcome === null
      ? null
      : (receipt.parts.join(" · ") || "Nessun documento inviato") +
        "." +
        (ignored > 0
          ? ` ${ignored} ${plural(ignored, "non è stato accettato", "non sono stati accettati")}: aggiorna la pagina e riprova.`
          : "");

  return {
    excluded,
    locked,
    isSelected: (id) => selected.has(id),
    toggle,
    selected: selectedIds,
    summary,
    submit,
    isPending: decide.isPending,
    done,
    notSent: receipt.notSent,
    receiptTone: ignored > 0 && receipt.tone === "ok" ? "err" : receipt.tone,
    error,
  };
}
